import {Injectable} from '@angular/core';
import {CanActivate, ActivatedRouteSnapshot, Router, RouterStateSnapshot} from '@angular/router';
import {AuthenticationService} from './authentication.service';
import {Observable} from 'rxjs/Observable';
import {AccessToken} from '../../models/access-token';
import {AccessTokenService} from '../access-token/access-token.service';

/**
 * Used by the router to check if the user has access to a given route.
 */
@Injectable()
export class AuthenticatedGuard implements CanActivate {
  constructor(private authenticationService: AuthenticationService,
              private tokenService: AccessTokenService<AccessToken>,
              private router: Router) {

  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.isAuthorized(route.params.agencyISIL.toUpperCase(), state.url);
  }

  /**
   * Checks if a route can be access by the current user.
   * It checks for:
   *  - A token.
   *  - The route matches the agency that the user is logged in under.
   *
   *  If the user does not have access, he is redirected to the login page for the agency he is trying to access.
   *  A redirect query parameter is set so that the login page can redirect back after the user has logged in.
   *
   * @param agencyISIL The ISIL of the agency that is being accessed.
   * @param redirectTo An URL to redirect to after logging in.
   * @returns {boolean}
   */
  private isAuthorized(agencyISIL: string, redirectTo?: string): Observable<boolean> {
    if (!agencyISIL) {
      return Observable.of(false);
    }

    return this.authenticationService.user
      .combineLatest(this.tokenService.decodedToken)
      .map(([user, token]) => user && token && user.agency.ISIL === agencyISIL)
      .do(isAuthorized => {
        if (!isAuthorized) {
          this.router.navigate([`/agency/${agencyISIL}/login`], {
            queryParams: {redirectTo},
            replaceUrl: true
          });
        }
      });
  }
}



// WEBPACK FOOTER //
// ./src/app/authentication/shared/authenticated.guard.ts